import { useEffect, useState } from "react";
import { logger } from "../lib/logger";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertCircle, Check, Code, Copy, Key, Loader2, Plus, Trash2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { AppShell, PageLoader } from "../components/AppShell";
import { api, formatError, BACKEND_URL } from "../lib/api";

function ExampleSnippets() {
  const base = `${BACKEND_URL}/api/v1`;
  const curl = `curl -X POST "${base}/generate" \\
  -H "X-API-Key: YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"template":"blog_post","topic":"AI in marketing","tone":"persuasive","length":"medium"}'`;

  const js = `const r = await fetch("${base}/generate", {
  method: "POST",
  headers: { "X-API-Key": process.env.COPYFORGE_KEY, "Content-Type": "application/json" },
  body: JSON.stringify({ template: "blog_post", topic: "AI in marketing", tone: "persuasive", length: "medium" }),
});
const { output, word_count, remaining_credits } = await r.json();`;

  const copy = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success("Copied"), (err) => {
      logger.error(err);
      toast.error("Couldn't copy");
    });
  };

  return (
    <div className="space-y-4 mt-6">
      <h3 className="text-lg font-bold text-zinc-900 dark:text-white font-heading">Quickstart</h3>
      {[
        { title: "cURL", code: curl },
        { title: "JavaScript / fetch", code: js },
      ].map((ex) => (
        <div key={ex.title} className="bg-zinc-950 text-zinc-100 p-4 font-mono text-xs relative" data-testid={`api-snippet-${ex.title}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold tracking-wider text-zinc-400 uppercase">{ex.title}</span>
            <button onClick={() => copy(ex.code)} className="text-zinc-400 hover:text-white text-xs flex items-center gap-1"><Copy className="w-3 h-3" /> Copy</button>
          </div>
          <pre className="whitespace-pre-wrap break-all">{ex.code}</pre>
        </div>
      ))}
      <div className="bg-zinc-100 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 p-4 text-sm">
        <p className="font-semibold text-zinc-900 dark:text-white mb-2">Available endpoints</p>
        <ul className="text-zinc-600 dark:text-zinc-400 space-y-1 font-mono text-xs">
          <li>POST {base}/generate</li>
          <li>POST {base}/refine</li>
          <li>POST {base}/social/hashtags</li>
          <li>POST {base}/social/captions</li>
          <li>GET&nbsp; {base}/templates</li>
          <li>GET&nbsp; {base}/usage</li>
        </ul>
        <p className="text-xs text-zinc-500 mt-2">Rate limit: 60 req/min per key · Credits deducted per call same as dashboard.</p>
      </div>
    </div>
  );
}

export default function DeveloperPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/api-keys");
      setKeys(data);
    } catch (error) {
      logger.error("Load api keys", error);
      setErr(formatError(error.response?.data?.detail) || "Failed to load keys");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setCreating(true);
    setErr("");
    try {
      const { data } = await api.post("/api-keys", { name });
      setNewKey(data);
      setName("");
      load();
    } catch (error) {
      logger.error("Create key", error);
      setErr(formatError(error.response?.data?.detail) || "Failed");
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this API key? This cannot be undone.")) return;
    try {
      await api.delete(`/api-keys/${id}`);
      setKeys((prev) => prev.filter((k) => k.id !== id));
      toast.success("Key revoked");
    } catch (error) {
      logger.error("Revoke key", error);
      toast.error("Failed to revoke");
    }
  };

  if (loading) return <PageLoader />;

  const needsSub = err && err.toLowerCase().includes("subscription") && user?.role !== "admin";

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-8" data-testid="developer-page">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 bg-zinc-950 flex items-center justify-center">
            <Code className="w-5 h-5 text-brand" />
          </div>
          <div>
            <h1 className="text-3xl font-black tracking-tighter text-zinc-950 dark:text-white font-heading">Developer API</h1>
            <p className="text-sm text-zinc-500">Programmatic access to CopyForge — $99/mo plan</p>
          </div>
        </div>

        {needsSub && (
          <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 p-6 mb-6" data-testid="upgrade-needed">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-amber-900 dark:text-amber-200 mb-1">Developer subscription required</h3>
                <p className="text-sm text-amber-800 dark:text-amber-300 mb-3">Upgrade to the Developer plan ($99/mo · 1,000 credits) to create API keys.</p>
                <button onClick={() => nav("/subscription")} className="bg-brand text-white font-semibold px-5 py-2 text-sm hover:bg-brand-hover" data-testid="go-subscribe-btn">View Plans</button>
              </div>
            </div>
          </div>
        )}

        {newKey && (
          <div className="bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 p-5 mb-6 animate-fade-in" data-testid="new-key-banner">
            <div className="flex items-start gap-3">
              <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-bold text-green-900 dark:text-green-200 mb-1">Key created. Copy it now — you won't see it again.</h3>
                <div className="flex items-center gap-2 mt-2">
                  <code className="flex-1 bg-white dark:bg-zinc-900 border border-green-300 dark:border-green-700 px-3 py-2 text-xs font-mono break-all" data-testid="new-key-value">{newKey.key}</code>
                  <button onClick={() => {
                    navigator.clipboard.writeText(newKey.key).then(() => toast.success("Copied"), (e) => { logger.error(e); toast.error("Copy failed"); });
                  }} className="bg-brand text-white px-3 py-2 text-xs font-semibold">Copy</button>
                </div>
                <button onClick={() => setNewKey(null)} className="text-xs font-semibold text-green-700 dark:text-green-300 hover:underline mt-3" data-testid="dismiss-new-key">I saved it</button>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-6 mb-6">
          <h2 className="text-lg font-bold text-zinc-900 dark:text-white mb-4 font-heading flex items-center gap-2"><Key className="w-5 h-5 text-brand" /> Your API keys</h2>
          <form onSubmit={create} className="flex gap-2 mb-5" data-testid="create-key-form">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Key name (e.g., Production server)" maxLength={60} className="flex-1 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 px-4 py-2.5 text-sm text-zinc-900 dark:text-white outline-none focus:border-brand" data-testid="new-key-name-input" />
            <button type="submit" disabled={creating || !name.trim()} data-testid="create-key-btn" className="bg-brand text-white font-semibold px-5 py-2.5 text-sm hover:bg-brand-hover flex items-center gap-2 disabled:opacity-50">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Create key
            </button>
          </form>
          {keys.length === 0 ? (
            <p className="text-sm text-zinc-400 text-center py-6">No keys yet. Create one to start using the API.</p>
          ) : (
            <div className="space-y-2">
              {keys.map((k) => (
                <div key={k.id} className="flex items-center justify-between border border-zinc-200 dark:border-zinc-700 p-3" data-testid={`key-row-${k.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-sm text-zinc-900 dark:text-white truncate">{k.name}</p>
                    <p className="text-xs text-zinc-500 font-mono">{k.preview} · created {new Date(k.created_at).toLocaleDateString()}{k.last_used ? ` · last used ${new Date(k.last_used).toLocaleDateString()}` : ""}</p>
                  </div>
                  <button onClick={() => revoke(k.id)} data-testid={`revoke-key-${k.id}`} className="text-red-500 hover:bg-red-50 dark:hover:bg-red-950 p-2">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <ExampleSnippets />
      </div>
    </AppShell>
  );
}
